import getQueryParams from 'src/functions/getQueryParams';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';

const FEATURE_FLAGS = {
  i18n_debug: {
    enabled: false,
    description: 'Enables debug logging for i18n system',
  },
  momentum_article_improvements: {
    enabled: true,
    description: 'Enables improvements to the Momentum article page',
  },
  request_a_demo_form_wizard: {
    enabled: true,
    description:
      'Replaces the /request-a-demo-form hero Demio embed with a 2-step wizard (HubSpot form + Live/On-Demand selector → Demio or on-demand page)',
  },
  request_a_call_simplified_form: {
    enabled: true,
    description:
      'Simplified Request-a-Call / Pro Custom Demo form: removes the goals tab and several business fields, and renames the submit button to "Book my demo"',
  },
  network_switcher_sign_in: {
    enabled: false,
    description:
      'Sign-in page: emailed verification code that lands the member signed in, instead of the legacy email listing their networks',
  },
};

type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

const getDefaultValue = (featureName: FeatureFlagKey): boolean => FEATURE_FLAGS[featureName]?.enabled;

const getFeatureFlagValue = (featureName: FeatureFlagKey): boolean => {
  const sessionStorageValue = getSessionStorage(getSessionStorageKey(featureName));

  if (sessionStorageValue) {
    return sessionStorageValue === 'true';
  }

  return getDefaultValue(featureName);
};

const getSessionStorageKey = (featureName: FeatureFlagKey): string => `@featureFlag:${featureName}`;

const storeSessionOverride = (featureName: FeatureFlagKey, enabled: boolean): void => {
  const sessionStorageKey = getSessionStorageKey(featureName);

  setSessionStorage(sessionStorageKey, enabled.toString());
};

const useFeatureFlag = (featureName: FeatureFlagKey): boolean => {
  // Gets default value for the feature flag on build time
  if (typeof window === 'undefined') return getDefaultValue(featureName);

  const { ff_on: featureFlagOnOverride, ff_off: featureFlagOffOverride } = getQueryParams();

  // Override feature flag value and store it in the local storage
  if (featureFlagOnOverride && decodeURIComponent(featureFlagOnOverride) === featureName) {
    storeSessionOverride(featureName, true);
  }

  if (featureFlagOffOverride && decodeURIComponent(featureFlagOffOverride) === featureName) {
    storeSessionOverride(featureName, false);
  }

  return getFeatureFlagValue(featureName);
};

export default useFeatureFlag;
